/**
 * HigherGovTab — "3rd Party" feed inside Marketplace Sentinel.
 *
 * Pulls from /api/highergov/opportunities (stored from HigherGov API).
 * Sync is triggered via triggerSync() called from the parent header button.
 * Mirrors the SAM Sentinel table layout: filter bar + sortable table + detail pane.
 */
import React, { forwardRef, useCallback, useEffect, useImperativeHandle, useState } from 'react';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { Input } from '../ui/input';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '../ui/select';
import {
  Loader2, ExternalLink, CheckCircle2, Globe,
  Building2, DollarSign, Calendar, Search, X,
  Trophy, Medal, Award, ChevronDown, Brain,
  OctagonX, CircleCheck, Layers,
  ChevronsUpDown, ChevronUp,
} from 'lucide-react';
import { toast } from 'sonner';
import { cn } from '../../lib/utils';
import RankBadge from './RankBadge';
import { AI_RANK_ORDER, AI_RANK_CONFIG } from '../../lib/aiRank';
import { HG_SCOPE_CONFIG, HG_STAGE_CONFIG, parseHgOppCat } from '../../lib/higherGovCategory';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const MATCH_TIERS = {
  gold:   { label: 'Gold',   icon: Trophy, badgeClass: 'bg-amber-100 text-amber-700 border border-amber-200' },
  silver: { label: 'Silver', icon: Medal,  badgeClass: 'bg-slate-200 text-slate-700 border border-slate-400' },
  bronze: { label: 'Bronze', icon: Award,  badgeClass: 'bg-orange-100 text-orange-700 border border-orange-200' },
};

const PAGE_SIZE = 50;

// source_path is stored as a full URL (HigherGov API `path` field)

// Federal/State-Local scope + Live/Forecast/Grant stage pills, parsed from
// HigherGov's own `opp_cat` field (see lib/higherGovCategory.js).
function HgCategoryPills({ oppCat, className }) {
  const { scope, stage } = parseHgOppCat(oppCat);
  if (!scope && !stage) return null;
  return (
    <div className={cn('flex items-center gap-1 flex-wrap', className)}>
      {scope && (
        <Badge className={cn('text-[10px] font-medium px-1.5 py-0', HG_SCOPE_CONFIG[scope].cls)}>
          {HG_SCOPE_CONFIG[scope].label}
        </Badge>
      )}
      {stage && (
        <Badge className={cn('text-[10px] font-medium px-1.5 py-0', HG_STAGE_CONFIG[stage].cls)}>
          {HG_STAGE_CONFIG[stage].label}
        </Badge>
      )}
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
export default forwardRef(function HigherGovTab(
  { authToken, user, isConsultant, selectedClientId, clientBrandColors, onSyncStateChange, onLatestSyncChange },
  ref
) {
  const targetOrgId = isConsultant && selectedClientId ? selectedClientId : null;

  const [loading, setLoading]           = useState(true);
  const [syncing, setSyncing]           = useState(false);
  const [opps, setOpps]                 = useState([]);
  const [total, setTotal]               = useState(0);
  const [page, setPage]                 = useState(1);
  const [loadingMore, setLoadingMore]   = useState(false);
  const [selectedOpp, setSelectedOpp]   = useState(null);

  const [filterStatus, setFilterStatus] = useState('suggested');
  const [filterTier,   setFilterTier]   = useState('all');
  // Server-side, like tier/status — this list is paginated, so filtering here
  // would only narrow the current page.
  const [filterAiRank, setFilterAiRank] = useState('all');
  const [searchQuery,  setSearchQuery]  = useState('');

  const [sortField, setSortField] = useState('posted_date');
  const [sortDir,   setSortDir]   = useState('desc');

  const [rankingHg, setRankingHg] = useState(false);

  // ── fetch ────────────────────────────────────────────────────────────────

  const fetchOpps = useCallback(async (pg = 1, append = false) => {
    if (pg === 1 && !append) setLoading(true);
    try {
      const params = new URLSearchParams();
      if (targetOrgId) params.append('organization_id', targetOrgId);
      if (filterStatus !== 'all') params.append('status', filterStatus);
      if (filterTier   !== 'all') params.append('tier',   filterTier);
      if (filterAiRank !== 'all') params.append('ai_rank', filterAiRank);
      if (searchQuery.trim()) params.append('search', searchQuery.trim());
      params.append('page', String(pg));
      params.append('limit', String(PAGE_SIZE));

      const res = await fetch(`${API_URL}/api/highergov/opportunities?${params}`, {
        headers: { Authorization: `Bearer ${authToken}` },
      });
      if (!res.ok) throw new Error(`${res.status}`);
      const data = await res.json();
      const list = data.opportunities || [];
      setTotal(data.total ?? list.length);
      setPage(pg);
      setOpps(prev => append ? [...prev, ...list] : list);
      if (!append && list.length > 0) setSelectedOpp(prev => prev ?? list[0]);
    } catch (err) {
      console.error('HigherGov fetch error:', err);
    } finally {
      setLoading(false);
    }
  }, [authToken, targetOrgId, filterStatus, filterTier, filterAiRank, searchQuery]);

  useEffect(() => {
    setOpps([]);
    setPage(1);
    setSelectedOpp(null);
    fetchOpps(1);
  }, [targetOrgId, filterStatus, filterTier, filterAiRank]); // eslint-disable-line react-hooks/exhaustive-deps

  const loadMore = async () => {
    setLoadingMore(true);
    await fetchOpps(page + 1, true);
    setLoadingMore(false);
  };

  // ── sync ─────────────────────────────────────────────────────────────────

  const handleSync = useCallback(async () => {
    setSyncing(true);
    try {
      const params = new URLSearchParams();
      if (targetOrgId) params.append('organization_id', targetOrgId);
      const res = await fetch(`${API_URL}/api/highergov/sync?${params}`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${authToken}` },
      });
      const data = await res.json();
      if (!res.ok) {
        toast.error(data.detail || 'HigherGov sync failed');
        return;
      }
      const now = new Date().toISOString();
      onLatestSyncChange?.({ synced_at: now, new_count: data.new_count });
      toast.success(data.message || 'HigherGov sync complete');
      (data.truncated_searches || []).forEach(t => {
        toast.warning(
          `"${t.label}" is too broad — only the first ${t.cap} of ~${t.total_count} matches were synced`,
          {
            description: 'Narrow this saved search with more specific NAICS codes or keywords in HigherGov, then re-sync.',
            duration: 12000,
          }
        );
      });
      fetchOpps(1);
    } catch {
      toast.error('Network error during sync');
    } finally {
      setSyncing(false);
    }
  }, [authToken, targetOrgId, fetchOpps, onLatestSyncChange]);

  useEffect(() => { onSyncStateChange?.(syncing); }, [syncing, onSyncStateChange]);

  useImperativeHandle(ref, () => ({ triggerSync: handleSync }), [handleSync]);

  // ── AI Rank ───────────────────────────────────────────────────────────────

  const handleRankHg = useCallback(async () => {
    const orgId = targetOrgId || user?.organization_id;
    if (!orgId) return;
    setRankingHg(true);
    try {
      const res = await fetch(`${API_URL}/api/highergov/opportunities/ai-rank`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${authToken}` },
        body: JSON.stringify({ organization_id: orgId }),
      });
      const data = await res.json();
      if (res.ok) {
        toast.success(data.message || `Ranked ${data.ranked} opportunities`);
        fetchOpps(1);
      } else {
        toast.error(data.detail || 'Ranking failed');
      }
    } catch {
      toast.error('Ranking request failed');
    } finally {
      setRankingHg(false);
    }
  }, [authToken, targetOrgId, user, fetchOpps]);

  // ── claim / dismiss ───────────────────────────────────────────────────────

  const handleClaim = useCallback(async (oppId) => {
    const res = await fetch(`${API_URL}/api/highergov/opportunities/${oppId}/claim`, {
      method: 'POST',
      headers: { Authorization: `Bearer ${authToken}` },
    });
    const data = await res.json();
    if (!res.ok) { toast.error(data.detail || 'Claim failed'); return; }
    toast.success('Opportunity claimed — added to pursuit pipeline');
    setOpps(prev => prev.map(o => o.id === oppId ? { ...o, status: 'claimed', pursuit_id: data.pursuit_id } : o));
    setSelectedOpp(prev => prev?.id === oppId ? { ...prev, status: 'claimed', pursuit_id: data.pursuit_id } : prev);
  }, [authToken]);

  const handleDismiss = useCallback(async (oppId) => {
    const res = await fetch(`${API_URL}/api/highergov/opportunities/${oppId}/dismiss`, {
      method: 'POST',
      headers: { Authorization: `Bearer ${authToken}` },
    });
    if (!res.ok) { toast.error('Dismiss failed'); return; }
    toast.info('Dismissed');
    setOpps(prev => prev.map(o => o.id === oppId ? { ...o, status: 'dismissed' } : o));
    setSelectedOpp(prev => prev?.id === oppId ? { ...prev, status: 'dismissed' } : prev);
  }, [authToken]);

  // ── sort ─────────────────────────────────────────────────────────────────

  const toggleSort = (field) => {
    if (sortField === field) setSortDir(d => d === 'asc' ? 'desc' : 'asc');
    else { setSortField(field); setSortDir('asc'); }
  };

  const sorted = [...opps].sort((a, b) => {
    let va = a[sortField] ?? '';
    let vb = b[sortField] ?? '';
    if (sortField === 'ai_rank') { va = AI_RANK_ORDER[va] ?? 99; vb = AI_RANK_ORDER[vb] ?? 99; }
    if (va < vb) return sortDir === 'asc' ? -1 : 1;
    if (va > vb) return sortDir === 'asc' ? 1 : -1;
    return 0;
  });

  const filtered = searchQuery.trim()
    ? sorted.filter(o =>
        o.title?.toLowerCase().includes(searchQuery.toLowerCase()) ||
        o.agency_name?.toLowerCase().includes(searchQuery.toLowerCase())
      )
    : sorted;

  function SortIcon({ field }) {
    if (sortField !== field) return <ChevronsUpDown className="w-3 h-3 ml-1 text-slate-400" />;
    return sortDir === 'asc'
      ? <ChevronUp className="w-3 h-3 ml-1 text-teal-600" />
      : <ChevronDown className="w-3 h-3 ml-1 text-teal-600" />;
  }

  // ── helpers ───────────────────────────────────────────────────────────────

  function formatCurrency(low, high) {
    const fmt = v => new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD', maximumFractionDigits: 0 }).format(v);
    if (!low && !high) return 'TBD';
    if (low && high && low !== high) return `${fmt(low)} – ${fmt(high)}`;
    return fmt(high || low);
  }

  function getDaysUntil(dateStr) {
    if (!dateStr) return null;
    return Math.ceil((new Date(dateStr) - new Date()) / 86400000);
  }

  // ── render ────────────────────────────────────────────────────────────────

  return (
    <div className="flex-1 flex flex-col overflow-hidden">

      {/* Filter bar */}
      <div className="flex-shrink-0 px-5 py-2 border-b border-slate-100 flex items-center gap-2">
        <div className="relative">
          <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-slate-500" />
          <Input
            placeholder="Search title or agency…"
            value={searchQuery}
            onChange={e => setSearchQuery(e.target.value)}
            onKeyDown={e => e.key === 'Enter' && fetchOpps(1)}
            className="pl-8 h-8 w-72 bg-white border-slate-200 text-slate-900 placeholder:text-slate-400 text-xs"
          />
        </div>
        <Select value={filterTier} onValueChange={v => { setFilterTier(v); setPage(1); }}>
          <SelectTrigger className="h-8 w-28 bg-white border-slate-200 text-xs">
            <SelectValue placeholder="All Tiers" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Tiers</SelectItem>
            <SelectItem value="gold">Gold</SelectItem>
            <SelectItem value="silver">Silver</SelectItem>
            <SelectItem value="bronze">Bronze</SelectItem>
          </SelectContent>
        </Select>
        <Select value={filterAiRank} onValueChange={v => { setFilterAiRank(v); setPage(1); }}>
          <SelectTrigger className="h-8 w-32 bg-white border-slate-200 text-xs" data-testid="hg-ai-rank-filter">
            <SelectValue placeholder="AI Fit" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All AI Fit</SelectItem>
            {Object.entries(AI_RANK_CONFIG).map(([key, cfg]) => (
              <SelectItem key={key} value={key}>
                <span className="flex items-center gap-2">
                  <span className={`w-2 h-2 rounded-full ${cfg.dot}`} />
                  {cfg.label}
                </span>
              </SelectItem>
            ))}
            <SelectItem value="unranked">
              <span className="flex items-center gap-2">
                <span className="w-2 h-2 rounded-full border border-slate-300" />
                Unranked
              </span>
            </SelectItem>
          </SelectContent>
        </Select>
        <Select value={filterStatus} onValueChange={v => { setFilterStatus(v); setPage(1); }}>
          <SelectTrigger className="h-8 w-28 bg-white border-slate-200 text-xs">
            <SelectValue placeholder="Status" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All</SelectItem>
            <SelectItem value="suggested">New</SelectItem>
            <SelectItem value="claimed">Claimed</SelectItem>
            <SelectItem value="dismissed">Dismissed</SelectItem>
          </SelectContent>
        </Select>
        <div className="flex-1" />
        <Button
          onClick={handleRankHg}
          disabled={rankingHg || opps.length === 0}
          variant="outline"
          className="h-8 px-3 text-xs border-purple-300 text-purple-700 hover:bg-purple-50 gap-1.5"
          title="AI-score each open opportunity against this client's portfolio"
          data-testid="ai-rank-hg-btn"
        >
          {rankingHg
            ? <><Loader2 className="w-3 h-3 animate-spin" />Ranking…</>
            : <><Brain className="w-3 h-3" />AI Rank</>}
        </Button>
        <span className="text-[11px] text-slate-400">{filtered.length} result{filtered.length !== 1 ? 's' : ''}</span>
      </div>

      {/* Table + detail pane */}
      <div className="flex-1 flex overflow-hidden">

        {/* Left column: scrollable table + persistent footer */}
        <div className="flex-1 flex flex-col overflow-hidden">
          <div className="flex-1 overflow-y-auto">
            {loading ? (
              <div className="flex items-center justify-center h-32">
                <Loader2 className="w-6 h-6 text-indigo-600 animate-spin" />
              </div>
            ) : filtered.length === 0 ? (
              <div className="flex flex-col items-center justify-center h-64 text-center p-6">
                <div className="w-12 h-12 bg-slate-100 rounded-full flex items-center justify-center mb-3">
                  <Globe className="w-6 h-6 text-slate-400" />
                </div>
                <p className="text-slate-400 font-medium text-sm">No HigherGov opportunities</p>
                <p className="text-slate-500 text-xs mt-1 max-w-[280px]">
                  {filterStatus === 'suggested'
                    ? 'Click "Sync HigherGov" to pull from your saved searches.'
                    : 'Try adjusting your filters.'}
                </p>
              </div>
            ) : (
              <table className="w-full text-sm border-collapse">
                <thead>
                  <tr className="text-[11px] font-medium text-slate-500 uppercase tracking-wider border-b border-slate-200 sticky top-0 bg-white z-10">
                    {[
                      { label: 'AI Fit',       field: 'ai_rank',     cls: 'w-[72px] text-left' },
                      { label: 'Due Date',     field: 'due_date',    cls: 'w-[115px] text-left' },
                      { label: 'Tier',         field: 'match_tier',  cls: 'w-[88px] text-left' },
                      { label: 'Title · Agency', field: 'title',     cls: 'text-left' },
                      { label: 'NAICS',        field: 'naics_code',  cls: 'w-[70px] text-left' },
                      { label: 'Type',         field: 'source_type', cls: 'w-[130px] text-left' },
                      { label: 'Value',        field: 'val_est_high',cls: 'w-[120px] text-right' },
                    ].map(({ label, field, cls }) => (
                      <th key={label}
                        className={cn('px-4 py-2.5', cls, 'cursor-pointer select-none hover:text-slate-700')}
                        onClick={() => toggleSort(field)}
                      >
                        <span className="inline-flex items-center">
                          {label}<SortIcon field={field} />
                        </span>
                      </th>
                    ))}
                    <th className="w-8" />
                  </tr>
                </thead>
                <tbody>
                  {filtered.map(opp => (
                    <HgTableRow
                      key={opp.id}
                      opp={opp}
                      isSelected={selectedOpp?.id === opp.id}
                      onSelect={() => setSelectedOpp(prev => prev?.id === opp.id ? null : opp)}
                      formatCurrency={formatCurrency}
                      getDaysUntil={getDaysUntil}
                    />
                  ))}
                </tbody>
              </table>
            )}
          </div>

          {/* Persistent footer */}
          {!loading && total > 0 && (
            <div className="flex-shrink-0 flex items-center justify-between px-4 py-2 border-t border-slate-200 bg-white">
              <span className="text-xs text-slate-500">
                Showing <span className="font-medium text-slate-700">{opps.length}</span> of <span className="font-medium text-slate-700">{total}</span> opportunities
              </span>
              {opps.length < total && (
                <Button variant="outline" size="sm" onClick={loadMore} disabled={loadingMore}
                  className="h-7 text-xs gap-1.5">
                  {loadingMore ? <Loader2 className="w-3 h-3 animate-spin" /> : <ChevronDown className="w-3 h-3" />}
                  {loadingMore ? 'Loading…' : 'Load more'}
                </Button>
              )}
            </div>
          )}
        </div>

        {/* Detail pane */}
        {selectedOpp && (
          <div className="w-[460px] flex-shrink-0 border-l border-slate-200 overflow-y-auto">
            <HgOppDetail
              opp={selectedOpp}
              onClaim={handleClaim}
              onDismiss={handleDismiss}
              onClose={() => setSelectedOpp(null)}
              formatCurrency={formatCurrency}
              getDaysUntil={getDaysUntil}
              brandColors={clientBrandColors}
            />
          </div>
        )}
      </div>
    </div>
  );
});

// ─────────────────────────────────────────────────────────────────────────────
// Table row
// ─────────────────────────────────────────────────────────────────────────────

function HgTableRow({ opp, isSelected, onSelect, formatCurrency, getDaysUntil }) {
  const tierCfg   = MATCH_TIERS[opp.match_tier] || MATCH_TIERS.bronze;
  const TierIcon  = tierCfg.icon;
  const daysUntil = getDaysUntil(opp.due_date);
  const isUrgent  = daysUntil !== null && daysUntil >= 0 && daysUntil <= 7;
  const isWarning = daysUntil !== null && daysUntil > 7 && daysUntil <= 14;

  return (
    <tr
      onClick={onSelect}
      className={cn(
        'border-b border-slate-100 cursor-pointer transition-colors text-sm',
        isSelected ? 'bg-indigo-50' : 'hover:bg-slate-50',
        opp.status === 'dismissed' && 'opacity-50',
      )}
    >
      {/* AI Fit */}
      <td className={cn('px-4 py-3 w-[72px]', isSelected && 'border-l-2 border-l-indigo-500')}>
        {opp.ai_rank ? (
          <RankBadge
            rank={opp.ai_rank}
            score={opp.ai_rank_score}
            reasoning={opp.ai_rank_reasoning}
              basis={opp.ai_rank_basis}
            variant="compact"
          />
        ) : (
          <span className="text-slate-300 text-xs">—</span>
        )}
      </td>
      {/* Due date */}
      <td className="px-4 py-3 w-[115px]">
        <div className={cn('font-medium text-xs tabular-nums',
          isUrgent ? 'text-red-600' : isWarning ? 'text-amber-600' : 'text-slate-600')}>
          {opp.due_date
            ? new Date(opp.due_date).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })
            : '—'}
        </div>
        {daysUntil !== null && (
          <div className={cn('text-[10px] mt-0.5',
            isUrgent ? 'text-red-600' : isWarning ? 'text-amber-600' : 'text-slate-500')}>
            {daysUntil < 0 ? 'Closed' : daysUntil === 0 ? 'Today!' : `${daysUntil}d left`}
          </div>
        )}
      </td>
      {/* Tier */}
      <td className="px-4 py-3">
        <Badge className={cn('text-[10px] font-medium flex items-center gap-1 w-fit', tierCfg.badgeClass)}>
          <TierIcon className="w-3 h-3" />
          {tierCfg.label}
        </Badge>
      </td>
      {/* Title + Agency */}
      <td className="px-4 py-3 min-w-0">
        <div className="text-slate-900 font-medium text-sm leading-snug truncate" title={opp.title}>
          {opp.title}
        </div>
        <div className="text-slate-500 text-[11px] mt-0.5 truncate">
          {opp.agency_name || '—'}
        </div>
      </td>
      {/* NAICS */}
      <td className="px-4 py-3">
        <span className="font-mono text-xs text-slate-500">{opp.naics_code || '—'}</span>
      </td>
      {/* Federal/State-Local + Live/Forecast/Grant, falling back to raw source_type */}
      <td className="px-4 py-3">
        {opp.opp_cat ? (
          <HgCategoryPills oppCat={opp.opp_cat} />
        ) : opp.source_type ? (
          <Badge className="bg-slate-100 text-slate-600 border border-slate-200 text-[10px] font-normal capitalize">
            {opp.source_type}
          </Badge>
        ) : <span className="text-slate-300 text-xs">—</span>}
      </td>
      {/* Value */}
      <td className="px-4 py-3 text-right">
        <span className="text-xs font-medium text-slate-700 tabular-nums">
          {formatCurrency(opp.val_est_low, opp.val_est_high)}
        </span>
      </td>
      {/* chevron */}
      <td className="px-2">
        <ChevronDown className={cn('w-3.5 h-3.5 text-slate-400 transition-transform', isSelected && '-rotate-90')} />
      </td>
    </tr>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// Detail pane
// ─────────────────────────────────────────────────────────────────────────────

function HgOppDetail({ opp, onClaim, onDismiss, onClose, formatCurrency, getDaysUntil, brandColors }) {
  const [claiming,   setClaiming]  = useState(false);
  const [dismissing, setDismissing] = useState(false);

  const daysUntil = getDaysUntil(opp.due_date);
  const isClaimable = opp.status !== 'claimed' && opp.status !== 'dismissed';

  const tierCfg  = MATCH_TIERS[opp.match_tier] || MATCH_TIERS.bronze;
  const TierIcon = tierCfg.icon;

  const primaryColor = brandColors?.primary || '#6366f1'; // indigo default

  const hgUrl     = opp.hg_url     || null;  // HigherGov detail page (requires HG login)
  const sourceUrl = opp.source_url || null;  // Original agency source (SAM, eBuy, etc.)

  const handleClaim = async () => {
    setClaiming(true);
    await onClaim(opp.id);
    setClaiming(false);
  };
  const handleDismiss = async () => {
    setDismissing(true);
    await onDismiss(opp.id);
    setDismissing(false);
  };

  return (
    <div className="flex flex-col h-full">
      {/* Header */}
      <div className="flex-shrink-0 px-5 py-4 border-b border-slate-200"
           style={{ borderTopColor: primaryColor, borderTopWidth: 3 }}>
        <div className="flex items-start justify-between gap-2 mb-2">
          <h3 className="text-sm font-semibold text-slate-900 leading-snug flex-1">{opp.title}</h3>
          <button onClick={onClose} className="text-slate-400 hover:text-slate-600 flex-shrink-0 mt-0.5">
            <X className="w-4 h-4" />
          </button>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <Badge className={cn('text-[10px] font-medium flex items-center gap-1', tierCfg.badgeClass)}>
            <TierIcon className="w-3 h-3" />{tierCfg.label}
          </Badge>
          {opp.status === 'claimed' && (
            <Badge className="text-[10px] bg-emerald-100 text-emerald-700 border border-emerald-200 flex items-center gap-1">
              <CircleCheck className="w-3 h-3" /> Claimed
            </Badge>
          )}
          {opp.status === 'dismissed' && (
            <Badge className="text-[10px] bg-slate-100 text-slate-500 border border-slate-200 flex items-center gap-1">
              <OctagonX className="w-3 h-3" /> Dismissed
            </Badge>
          )}
          {opp.opp_cat ? (
            <HgCategoryPills oppCat={opp.opp_cat} />
          ) : opp.source_type ? (
            <Badge className="text-[10px] bg-slate-100 text-slate-600 border border-slate-200 capitalize">
              {opp.source_type}
            </Badge>
          ) : null}
        </div>
        {opp.ai_rank && (
          <div className="mt-2 space-y-1.5">
            <RankBadge
              rank={opp.ai_rank}
              score={opp.ai_rank_score}
              reasoning={opp.ai_rank_reasoning}
              basis={opp.ai_rank_basis}
              variant="detail"
            />
          </div>
        )}
      </div>

      {/* Scrollable body */}
      <div className="flex-1 overflow-y-auto px-5 py-4 space-y-4">

        {/* Meta row */}
        <div className="grid grid-cols-2 gap-3">
          {opp.agency_name && (
            <div className="flex items-start gap-2">
              <Building2 className="w-3.5 h-3.5 text-slate-400 mt-0.5 flex-shrink-0" />
              <div>
                <p className="text-[10px] text-slate-400 uppercase tracking-wider">Agency</p>
                <p className="text-xs text-slate-700 mt-0.5">{opp.agency_name}</p>
              </div>
            </div>
          )}
          {opp.due_date && (
            <div className="flex items-start gap-2">
              <Calendar className="w-3.5 h-3.5 text-slate-400 mt-0.5 flex-shrink-0" />
              <div>
                <p className="text-[10px] text-slate-400 uppercase tracking-wider">Due Date</p>
                <p className={cn('text-xs mt-0.5',
                  daysUntil !== null && daysUntil >= 0 && daysUntil <= 7 ? 'text-red-600 font-medium'
                  : daysUntil !== null && daysUntil <= 14 ? 'text-amber-600'
                  : 'text-slate-700')}>
                  {new Date(opp.due_date).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}
                  {daysUntil !== null && daysUntil >= 0 && (
                    <span className="ml-1 text-[10px]">({daysUntil}d)</span>
                  )}
                </p>
              </div>
            </div>
          )}
          {opp.naics_code && (
            <div className="flex items-start gap-2">
              <Layers className="w-3.5 h-3.5 text-slate-400 mt-0.5 flex-shrink-0" />
              <div>
                <p className="text-[10px] text-slate-400 uppercase tracking-wider">NAICS</p>
                <p className="text-xs text-slate-700 font-mono mt-0.5">{opp.naics_code}</p>
              </div>
            </div>
          )}
          {(opp.val_est_low || opp.val_est_high) && (
            <div className="flex items-start gap-2">
              <DollarSign className="w-3.5 h-3.5 text-slate-400 mt-0.5 flex-shrink-0" />
              <div>
                <p className="text-[10px] text-slate-400 uppercase tracking-wider">Est. Value</p>
                <p className="text-xs text-slate-700 mt-0.5">{formatCurrency(opp.val_est_low, opp.val_est_high)}</p>
              </div>
            </div>
          )}
          {opp.set_aside && (
            <div className="flex items-start gap-2">
              <CheckCircle2 className="w-3.5 h-3.5 text-slate-400 mt-0.5 flex-shrink-0" />
              <div>
                <p className="text-[10px] text-slate-400 uppercase tracking-wider">Type</p>
                <p className="text-xs text-slate-700 mt-0.5">
                  {typeof opp.set_aside === 'string' ? opp.set_aside : opp.set_aside?.description || ''}
                </p>
              </div>
            </div>
          )}
          {opp.search_label && (
            <div className="flex items-start gap-2">
              <Globe className="w-3.5 h-3.5 text-slate-400 mt-0.5 flex-shrink-0" />
              <div>
                <p className="text-[10px] text-slate-400 uppercase tracking-wider">Search</p>
                <p className="text-xs text-slate-700 mt-0.5">{opp.search_label}</p>
              </div>
            </div>
          )}
        </div>

        {/* AI summary */}
        {opp.ai_summary && (
          <div className="bg-violet-50 border border-violet-200 rounded-lg p-3">
            <p className="text-[10px] font-semibold text-violet-700 uppercase tracking-wider mb-1.5">HigherGov AI Summary</p>
            <p className="text-xs text-violet-900 leading-relaxed">{opp.ai_summary}</p>
          </div>
        )}

        {/* Description */}
        {opp.description_text && (
          <div>
            <p className="text-[10px] font-semibold text-slate-500 uppercase tracking-wider mb-1.5">Description</p>
            <p className="text-xs text-slate-700 leading-relaxed whitespace-pre-wrap line-clamp-[12]">
              {opp.description_text}
            </p>
          </div>
        )}

        {/* External links */}
        <div className="flex flex-wrap gap-3">
          {sourceUrl && (
            <a href={sourceUrl} target="_blank" rel="noopener noreferrer"
               className="inline-flex items-center gap-1.5 text-xs text-indigo-600 hover:text-indigo-800 hover:underline">
              <ExternalLink className="w-3.5 h-3.5" />
              View Source
            </a>
          )}
          {hgUrl && (
            <a href={hgUrl} target="_blank" rel="noopener noreferrer"
               className="inline-flex items-center gap-1.5 text-xs text-slate-500 hover:text-slate-700 hover:underline">
              <Globe className="w-3.5 h-3.5" />
              View on HigherGov
            </a>
          )}
        </div>
      </div>

      {/* Action footer */}
      {isClaimable && (
        <div className="flex-shrink-0 px-5 py-3 border-t border-slate-200 flex gap-2">
          <Button
            className="flex-1 h-8 text-xs gap-1.5 text-white"
            style={{ backgroundColor: primaryColor }}
            onClick={handleClaim}
            disabled={claiming}
          >
            {claiming ? <Loader2 className="w-3 h-3 animate-spin" /> : <CircleCheck className="w-3 h-3" />}
            {claiming ? 'Claiming…' : 'Claim → Pipeline'}
          </Button>
          <Button variant="outline" size="sm"
            className="h-8 text-xs border-slate-300 text-slate-600 hover:text-slate-900 gap-1.5"
            onClick={handleDismiss}
            disabled={dismissing}
          >
            {dismissing ? <Loader2 className="w-3 h-3 animate-spin" /> : <OctagonX className="w-3 h-3" />}
            Dismiss
          </Button>
        </div>
      )}
      {opp.status === 'claimed' && opp.pursuit_id && (
        <div className="flex-shrink-0 px-5 py-3 border-t border-slate-200">
          <p className="text-xs text-emerald-700 flex items-center gap-1.5">
            <CheckCircle2 className="w-3.5 h-3.5" />
            Added to pursuit pipeline
          </p>
        </div>
      )}
    </div>
  );
}
